import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, KeyRound, ShieldCheck, Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { usePromoAuth } from "../context/AuthContext";

export default function ChangePassword() {
  const { account, refresh } = usePromoAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const forced = !!account?.must_change_password;

  const checks = useMemo(() => ({
    length: next.length >= 8,
    upper:  /[A-Z]/.test(next),
    digit:  /[0-9]/.test(next),
    symbol: /[^A-Za-z0-9]/.test(next),
    match:  next.length > 0 && next === confirm,
  }), [next, confirm]);

  const score = (checks.length ? 1 : 0) + (checks.upper ? 1 : 0) + (checks.digit ? 1 : 0) + (checks.symbol ? 1 : 0);
  const strengthLabel = ["Too weak", "Weak", "Okay", "Strong", "Excellent"][score];
  const strengthColor = ["#ef4444", "#f59e0b", "#facc15", "#22c073", "#10b981"][score];

  const submit = async (e) => {
    e.preventDefault();
    if (!next || !confirm) return toast.error("Please enter your new password");
    if (!forced && !current) return toast.error("Current password required");
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const payload = { new_password: next };
      if (!forced) payload.current_password = current;
      await api.post("/aff-panel/auth/change-password", payload);
      toast.success("Password updated");
      await refresh();
      navigate("/promo/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  const Wrapper = forced ? ForcedWrapper : InlineWrapper;

  return (
    <Wrapper>
      <div className="cp-card">
        <div className="cp-card-ambient" aria-hidden />
        <div className="cp-card-body">
          {/* Header */}
          <div className="cp-head">
            <div className="cp-icon">
              <ShieldCheck size={22} />
            </div>
            <div className="cp-chip">
              {forced ? "Required · First login" : "Account security"}
            </div>
            <h2 className="cp-title">{forced ? "Set your new password" : "Change password"}</h2>
            <p className="cp-sub">
              {forced
                ? "Choose a strong new password to activate and secure your premium account."
                : "Update your password periodically to keep your account secure."}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="cp-form" data-testid="change-password-form">
            {!forced && (
              <Field label="Current Password">
                <Lock size={14} className="cp-input-ico" />
                <input
                  type={show ? "text" : "password"}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  placeholder="••••••••"
                  data-testid="cp-current"
                  autoComplete="current-password"
                />
              </Field>
            )}

            <Field label="New Password">
              <KeyRound size={14} className="cp-input-ico" />
              <input
                type={show ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="min 8 chars"
                data-testid="cp-new"
                autoComplete="new-password"
              />
              <button type="button" onClick={() => setShow(!show)} className="cp-eye" aria-label="toggle visibility">
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </Field>

            {/* Strength meter */}
            {next && (
              <div className="cp-strength">
                <div className="cp-bars">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="cp-bar"
                      style={{ background: i < score ? strengthColor : "rgba(255,255,255,0.07)" }} />
                  ))}
                </div>
                <div className="cp-strength-row">
                  <span className="cp-strength-lbl" style={{ color: strengthColor }}>{strengthLabel}</span>
                  <span className="cp-strength-hint">{score < 4 ? "Mix upper, digits & symbols." : "Looks great!"}</span>
                </div>
                <div className="cp-checklist">
                  <Rule ok={checks.length}  label="8+ characters" />
                  <Rule ok={checks.upper}   label="Uppercase" />
                  <Rule ok={checks.digit}   label="Number" />
                  <Rule ok={checks.symbol}  label="Symbol" />
                  <Rule ok={checks.match}   label="Match" />
                </div>
              </div>
            )}

            <Field label="Confirm New Password">
              <KeyRound size={14} className="cp-input-ico" />
              <input
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="re-enter"
                data-testid="cp-confirm"
                autoComplete="new-password"
              />
            </Field>

            <button type="submit" disabled={busy} className="cp-submit" data-testid="cp-submit">
              <Sparkles size={14} /> {busy ? "Updating…" : forced ? "Activate Account" : "Save & Continue"}
            </button>

            <div className="cp-footnote">
              <Lock size={11} /> Your password is hashed with bcrypt — we never store the original.
            </div>
          </form>
        </div>
      </div>
      <style>{styles}</style>
    </Wrapper>
  );
}

function Field({ label, children }) {
  return (
    <div className="cp-field">
      <label className="cp-label">{label}</label>
      <div className="cp-input-shell">{children}</div>
    </div>
  );
}

function Rule({ ok, label }) {
  return (
    <span className={`cp-rule ${ok ? "is-ok" : ""}`}>
      {ok ? <Check size={11} /> : <X size={11} />} {label}
    </span>
  );
}

function ForcedWrapper({ children }) {
  return (
    <div className="cp-forced-bg">
      <div className="cp-orb cp-orb-a" aria-hidden />
      <div className="cp-orb cp-orb-b" aria-hidden />
      {children}
    </div>
  );
}

function InlineWrapper({ children }) {
  return <div className="cp-inline-wrap">{children}</div>;
}

/* ════════════════════════════════════════════════════════════════════════
   STYLES
   ════════════════════════════════════════════════════════════════════════ */
const styles = `
.cp-forced-bg {
  min-height: 100vh;
  display: flex; align-items: center; justify-content: center;
  padding: 24px 16px;
  position: relative; overflow: hidden;
  background: radial-gradient(ellipse at top, #1a1325 0%, #06080c 70%);
}
.cp-inline-wrap { max-width: 540px; margin: 0 auto; padding: 8px; }

.cp-orb {
  position: absolute; pointer-events: none; border-radius: 50%;
  filter: blur(70px); opacity: 0.5;
}
.cp-orb-a { top: -120px; left: -120px; width: 420px; height: 420px;
  background: radial-gradient(circle, rgba(34, 192, 115, 0.4), transparent 70%);
}
.cp-orb-b { bottom: -160px; right: -100px; width: 380px; height: 380px;
  background: radial-gradient(circle, rgba(212, 175, 55, 0.18), transparent 70%);
}

.cp-card {
  position: relative;
  width: 100%; max-width: 480px;
  border-radius: 24px;
  border: 1px solid rgba(34, 192, 115, 0.35);
  background: linear-gradient(180deg, rgba(10, 22, 16, 0.92), rgba(6, 8, 12, 0.98));
  box-shadow: 0 28px 70px -16px rgba(0,0,0,0.75), 0 0 0 1px rgba(34,192,115,0.06) inset;
  overflow: hidden;
  isolation: isolate;
}
.cp-card-ambient {
  position: absolute; inset: 0; pointer-events: none; z-index: 0;
  background:
    radial-gradient(450px 200px at 80% -10%, rgba(34, 192, 115, 0.22), transparent 60%),
    radial-gradient(360px 200px at -10% 110%, rgba(212, 175, 55, 0.14), transparent 60%);
}
.cp-card-body { position: relative; z-index: 1; padding: 28px 24px; }
@media (min-width: 540px) { .cp-card-body { padding: 36px 32px; } }

.cp-head { text-align: center; margin-bottom: 22px; }
.cp-icon {
  width: 56px; height: 56px; border-radius: 18px;
  margin: 0 auto 14px;
  display: inline-flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, rgba(34,192,115,0.25), rgba(34,192,115,0.04));
  border: 1px solid rgba(34, 192, 115, 0.5);
  color: #6ee7b7;
  box-shadow: 0 12px 28px -10px rgba(34, 192, 115, 0.5);
}
.cp-chip {
  display: inline-block;
  padding: 5px 12px; border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.22em; text-transform: uppercase;
  background: rgba(212,175,55,0.12);
  border: 1px solid rgba(212,175,55,0.4);
  color: #fde68a;
  margin-bottom: 12px;
}
.cp-title {
  font-size: clamp(22px, 5vw, 28px); font-weight: 900;
  background: linear-gradient(180deg, #fff7d6, #d4af37 60%, #22c073);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  letter-spacing: -0.01em; margin: 0;
}
.cp-sub {
  font-size: 13px; color: rgba(255,255,255,0.55);
  margin: 8px auto 0; max-width: 380px; line-height: 1.55;
}

.cp-form { display: flex; flex-direction: column; gap: 12px; }

.cp-field {}
.cp-label {
  display: block;
  font-size: 10px; font-weight: 800; letter-spacing: 0.2em; text-transform: uppercase;
  color: rgba(167,243,208,0.7); margin-bottom: 6px;
}
.cp-input-shell {
  display: flex; align-items: center; gap: 8px;
  height: 48px; padding: 0 14px;
  background: rgba(8,6,14,0.65);
  border: 1px solid rgba(34, 192, 115, 0.28);
  border-radius: 14px;
  transition: all 0.18s;
}
.cp-input-shell:focus-within { border-color: #22c073; box-shadow: 0 0 0 4px rgba(34,192,115,0.18); }
.cp-input-shell input {
  flex: 1; min-width: 0;
  background: transparent; outline: none; border: none;
  color: #f5f1e6; font-size: 14px; font-weight: 600;
}
.cp-input-ico { color: rgba(167,243,208,0.65); flex-shrink: 0; }
.cp-eye {
  background: transparent; border: none; cursor: pointer; padding: 4px;
  color: rgba(255,255,255,0.4);
}
.cp-eye:hover { color: #6ee7b7; }

.cp-strength { padding: 12px 14px; border-radius: 14px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); }
.cp-bars { display: flex; gap: 5px; margin-bottom: 8px; }
.cp-bar { flex: 1; height: 5px; border-radius: 999px; transition: background 250ms ease; }
.cp-strength-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.cp-strength-lbl { font-size: 12px; font-weight: 800; }
.cp-strength-hint { font-size: 11px; color: rgba(255,255,255,0.4); }
.cp-checklist { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 10px; }
.cp-rule {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 999px;
  font-size: 10px; font-weight: 700;
  background: rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.5);
  border: 1px solid rgba(255,255,255,0.06);
  transition: all 200ms ease;
}
.cp-rule.is-ok {
  background: rgba(34,192,115,0.13);
  border-color: rgba(34,192,115,0.4);
  color: #6ee7b7;
}

.cp-submit {
  width: 100%; height: 50px;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  border-radius: 14px; border: 1px solid rgba(34, 192, 115, 0.5);
  background: linear-gradient(135deg, #22c073 0%, #0ea05a 100%);
  color: #04130b; font-size: 14px; font-weight: 900;
  cursor: pointer; transition: all 0.18s;
  box-shadow: 0 12px 30px -8px rgba(34, 192, 115, 0.55), 0 0 0 1px rgba(34, 192, 115, 0.5) inset;
  margin-top: 8px;
}
.cp-submit:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.06); }
.cp-submit:disabled { opacity: 0.55; cursor: not-allowed; }

.cp-footnote {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  font-size: 10.5px; color: rgba(255,255,255,0.35);
  margin-top: 4px; text-align: center;
}
`;
